import * as msTeams from "@microsoft/teams-js";
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { ThemeProvider, createUseStyles } from "react-jss";
import { head, last, pipe, toPairs } from "ramda";
import Chat from "./AgentChat";
import Conversations from "./Conversations";
import Header from "./Header";
import connect from "./agentSocket";
import { eventTypes } from "../lib/analytics";
import getTheme from "../styles/Theme";
import { v4 as uuidv4 } from "uuid";

const useStyles = createUseStyles({
  "@global": {
    body: { height: "100%", width: "100%", margin: "0", overflow: "hidden" },
  },
  app: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, Roboto, "Segoe UI", Helvetica,Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol" !important',
    fontSize: "14px",
    color: "#151647",
    fontWeight: "400",
    "& p": {
      margin: "2px 0",
    },
  },
  header: {
    border: "1px solid rgba(135, 140, 151, 0.2)",
  },
  main: {
    display: "flex",
    flexDirection: "row",
    // TODO(reut): Figure out if we need this
    overflowY: "scroll",
  },
  conversations: {
    width: "70%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
  },
});

const useStylesLogIn = createUseStyles({
  background: {
    height: "100vh",
    width: "100hh",
    background: "#F6F6F6",
  },
  fail: {
    color: "#F93E3E",
    fontFamily: '-apple-system, BlinkMacSystemFont, Roboto, "Segoe UI", Roboto',
    fontStyle: "normal",
    fontWeight: "bold",
    fontSize: "16px",
    lineHeight: "24px",
    textAlign: "center",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  label: {
    composes: "$fail",
    color: "#464775",
    fontSize: "18px",
    margin: "0 50px 7px 0",
  },
});

const authTokenRequest = (setAgentToken) => ({
  successCallback: (result) => {
    setAgentToken(result);
  },
  failureCallback: (error) => {
    console.log("Failure getting agent token: " + error);
  },
});

const addEventToConversation = (
  setConversations,
  conversations,
  conversationId,
  event
) =>
  setConversations({
    ...conversations,
    [conversationId]: {
      conversationHistory: [
        ...conversations[conversationId].conversationHistory,
        event,
      ],
    },
  });

const sendUtterance =
  (setConversations, conversations, socketHandlers, conversationId) =>
  (utterance) => {
    addEventToConversation(setConversations, conversations, conversationId, {
      type: eventTypes.BOT_UTTERANCE,
      createdAt: new Date().toISOString(),
      data: {
        text: utterance,
        type: eventTypes.BOT_UTTERANCE,
        sender: "liveAgent",
      },
    });
    socketHandlers.sendAgentMessage(conversationId, utterance);
  };

const onAcceptConversation = (socketHandlers) => (conversationId) =>
  socketHandlers.acceptConversation(conversationId);

const onEndConversation = (socketHandlers) => (conversationId) =>
  socketHandlers.endConversation(conversationId);

const onSetAgentStatus =
  (socketHandlers, agentOnlineStatus, setAgentOnlineStatus) => () => {
    setAgentOnlineStatus(!agentOnlineStatus);
    socketHandlers.setAgentStatus(!agentOnlineStatus ? "online" : "offline");
  };

const eventHandlers = (
  setPendingConversations,
  setConversations,
  conversationsRef,
  pendingConversationsRef,
  addConnectionEvent,
  setAgentOnlineStatus,
  selectConversation,
  setConnectionMessage
) => ({
  addNewActiveConversation: (conversation) => {
    const [conversationId, conversationHistory] = pipe(
      toPairs,
      head
    )(conversation);
    setConversations({
      ...conversationsRef,
      [conversationId]: conversationHistory,
    });
    selectConversation(conversationId);
  },
  addEventToActiveConversation: ({ conversationId, event }) =>
    addEventToConversation(
      setConversations,
      conversationsRef,
      conversationId,
      event
    ),
  updatePendingConversationsList: (pendingConversations) =>
    setPendingConversations(pendingConversations),
  addNewPendingConversation: (conversation) => {
    const [conversationId, conversationHistory] = pipe(
      toPairs,
      head
    )(conversation);
    setPendingConversations({
      ...pendingConversationsRef,
      [conversationId]: conversationHistory,
    });
  },
  removeConversationFromPendingList: (conversationId) => {
    delete pendingConversationsRef[conversationId];
    setPendingConversations({ ...pendingConversationsRef });
  },
  connected: ({ pendingConversations, activeConversations }) => {
    setPendingConversations(pendingConversations);
    setConversations(activeConversations);
    addConnectionEvent(true);
    setConnectionMessage(null);
  },
  disconnect: () => addConnectionEvent(false),
  unauthorized: (message) => setConnectionMessage(message),
  agentOnlineStatus: (status) => setAgentOnlineStatus(status),
});

const LogIn = ({ message }) => {
  const classes = useStylesLogIn();
  return (
    <div className={classes.background}>
      {message ? <div className={classes.fail}>{message}</div> : null}
    </div>
  );
};

const tabId = uuidv4();

export default () => {
  const [conversations, setConversations] = useState({});
  const [pendingConversations, setPendingConversations] = useState({});
  const [socketHandlers, setSocketHandlers] = useState();
  const [selectedConversationId, setselectedConversationId] = useState();
  const [agentOnlineStatus, setAgentOnlineStatus] = useState(true);
  const [agentToken, setAgentToken] = useState();
  const [appEntityId, setAppEntityId] = useState();
  const [connectionMessage, setConnectionMessage] = useState("Connecting...");
  // TODO(reut): State is used in functions outside of the component and it is not stable. Figure out a better way.
  const conversationsRef = useRef(conversations);
  const pendingConversationsRef = useRef(pendingConversations);
  const [connectionEvents, addConnectionEvent] = useReducer(
    (state, current) => [...state, current],
    []
  );

  const styles = useStyles();
  const selectConversation = useCallback(
    (conversationId) => setselectedConversationId(conversationId),
    []
  );

  const makeEventHandlers = useCallback(
    (type, args) =>
      eventHandlers(
        setPendingConversations,
        setConversations,
        conversationsRef.current,
        pendingConversationsRef.current,
        addConnectionEvent,
        setAgentOnlineStatus,
        selectConversation,
        setConnectionMessage
      )[type](...args),
    [selectConversation]
  );

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    pendingConversationsRef.current = pendingConversations;
  }, [pendingConversations]);

  // Enable next line when testing on localhost without teams.
  // if (process.env.AGENT_APP_PLATFORM === "teams") {
  useEffect(() => {
    msTeams.initialize();
    msTeams.authentication.getAuthToken(authTokenRequest(setAgentToken));
    msTeams.getContext((context) => setAppEntityId(context.entityId));
  }, [setAgentToken, setAppEntityId]);
  // }

  useEffect(() => {
    if (
      !last(connectionEvents) &&
      // Enable next line when testing on localhost without teams.
      // process.env.AGENT_APP_PLATFORM !== "teams" ||
      agentToken &&
      appEntityId
    ) {
      const socket = connect(
        process.env.REACT_APP_SOCKET_SERVER,
        makeEventHandlers,
        appEntityId,
        agentToken,
        setConnectionMessage,
        tabId
      );

      setSocketHandlers({
        setAgentStatus: (status) => socket.emit("setAgentStatus", { status }),
        acceptConversation: (conversationId) =>
          socket.emit("acceptConversation", { conversationId, agentToken }),
        sendAgentMessage: (conversationId, text) =>
          socket.emit("agentMessage", {
            conversationId,
            type: eventTypes.BOT_UTTERANCE,
            data: { text, sender: "liveAgent" },
          }),
        endConversation: (conversationId) =>
          socket.emit("endConversation", conversationId),
      });
    }
  }, [
    connectionEvents,
    addConnectionEvent,
    agentToken,
    appEntityId,
    makeEventHandlers,
    setConnectionMessage,
  ]);

  useEffect(() => {
    if (conversations && socketHandlers && !selectedConversationId) {
      selectConversation(Object.keys(conversations)[0]);
    }
  }, [
    conversations,
    socketHandlers,
    selectedConversationId,
    selectConversation,
  ]);
  return (
    <ThemeProvider
      theme={{
        ...getTheme({
          "--main-color-palette": "gray",
          "--secondary-color-palette": "#464775",
          "--chat-background": "rgb(235, 237, 248)",
          "--avatar-color": "gray",
          "--chat-widget-light-white": "rgb(235, 237, 248, 0.08)",
        }),
        conversationTextColor: "#919191",
        agentColor: "#fff",
      }}
    >
      {socketHandlers && !connectionMessage ? (
        <div className={styles.app}>
          <div className={styles.header}>
            <Header
              onSetAgentStatus={onSetAgentStatus(
                socketHandlers,
                agentOnlineStatus,
                setAgentOnlineStatus
              )}
              agentOnlineStatus={agentOnlineStatus}
            />
          </div>
          <div className={styles.main}>
            <div className={styles.conversations}>
              <Conversations
                conversations={conversations}
                selectedConversationId={selectedConversationId}
                onSelectConversation={selectConversation}
                onAcceptOrEndConversation={onEndConversation(socketHandlers)}
                type="active"
                agentOnlineStatus={agentOnlineStatus}
              />
              <Conversations
                conversations={pendingConversations}
                onAcceptOrEndConversation={onAcceptConversation(socketHandlers)}
                type="incoming"
                agentOnlineStatus={agentOnlineStatus}
              />
            </div>
            <Chat
              sendUtterance={sendUtterance(
                setConversations,
                conversations,
                socketHandlers,
                selectedConversationId
              )}
              selectedConversation={
                selectedConversationId
                  ? conversations[selectedConversationId]
                  : null
              }
            />
          </div>
        </div>
      ) : (
        <LogIn message={connectionMessage} />
      )}
    </ThemeProvider>
  );
};
